import { Box, Paper, Typography } from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';
import { ROUTES } from '../../constants/routes';
import { useGuardedNavigate } from '../../hooks/useUnsavedGuard';
import type { SearchResults } from '../../services/searchService';

interface SearchDropdownProps {
  results: SearchResults;
  open: boolean;
  onClose: () => void;
}

function CategoryHeader({ label }: { label: string }) {
  return (
    <Typography variant="caption" sx={{ px: 2, pt: 1.5, pb: 0.5, display: 'block', fontWeight: 700, textTransform: 'uppercase', color: 'text.secondary' }}>
      {label}
    </Typography>
  );
}

export const SearchDropdown = ({ results, open, onClose }: SearchDropdownProps) => {
  const guardedNavigate = useGuardedNavigate();
  const theme = useTheme();

  if (!open) return null;

  const hoverSx = { px: 2, py: 0.75, cursor: 'pointer', '&:hover': { backgroundColor: alpha(theme.palette.primary.main, 0.06) } };
  const secondarySx = { color: 'text.secondary', fontSize: '0.75rem', ml: 1 };

  const isEmpty = !results.projects.length && !results.samples.length && !results.locations.length && !results.workOrders.length;

  const go = (path: string) => {
    guardedNavigate(path);
    onClose();
  };

  return (
    <Paper sx={{ position: 'absolute', zIndex: 1300, top: '100%', left: 0, right: 0, mt: 0.5, maxHeight: 400, overflowY: 'auto', boxShadow: 4 }}>
      {isEmpty ? (
        <Typography sx={{ p: 2, textAlign: 'center', color: 'text.secondary' }}>No results found</Typography>
      ) : (
        <>
          {results.projects.length > 0 && (
            <>
              <CategoryHeader label="Projects" />
              {results.projects.map((p) => (
                <Box key={p.ProjectKey} sx={hoverSx} onClick={() => go(ROUTES.LOCATIONS.replace(':projectId', p.ProjectId))}>
                  <Typography variant="body2">{p.ProjectId}<Typography component="span" sx={secondarySx}>{p.ProjectName}</Typography></Typography>
                </Box>
              ))}
            </>
          )}
          {results.samples.length > 0 && (
            <>
              <CategoryHeader label="Samples" />
              {results.samples.map((s) => (
                <Box key={s.SampleKey} sx={hoverSx} onClick={() => {
                  const params = new URLSearchParams();
                  if (s.LocationKey) params.set('location', s.LocationKey);
                  params.set('sample', s.SampleId);
                  go(ROUTES.LOCATIONS.replace(':projectId', s.ProjectId) + '?' + params.toString());
                }}>
                  <Typography variant="body2">{s.SampleId}<Typography component="span" sx={secondarySx}>{s.LocationName} · {s.ProjectId}</Typography></Typography>
                </Box>
              ))}
            </>
          )}
          {results.locations.length > 0 && (
            <>
              <CategoryHeader label="Locations" />
              {results.locations.map((l) => (
                <Box key={l.LocationKey} sx={hoverSx} onClick={() => go(ROUTES.LOCATIONS.replace(':projectId', l.ProjectId))}>
                  <Typography variant="body2">{l.LocationId}<Typography component="span" sx={secondarySx}>{l.LocationName}</Typography></Typography>
                </Box>
              ))}
            </>
          )}
          {results.workOrders.length > 0 && (
            <>
              <CategoryHeader label="Work Orders" />
              {results.workOrders.map((wo) => (
                <Box key={wo.WorkOrderKey} sx={hoverSx} onClick={() => go(ROUTES.WORK_ORDERS_TAB)}>
                  <Typography variant="body2">{wo.WorkOrderId}<Typography component="span" sx={secondarySx}>{wo.WorkOrderStatus}</Typography></Typography>
                </Box>
              ))}
            </>
          )}
        </>
      )}
    </Paper>
  );
};
